import React, { useEffect, useRef, useState } from "react";
import { X, Pencil, ArrowUpRight, Circle as CircleIcon, Square, TextT, ArrowClockwise, Trash, Check, Palette } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { tryLiveOrQueue, makeMarkupUpload } from "@/lib/offlineQueue";

/**
 * PhotoMarkupModal — CompanyCam-style photo markup editor.
 *
 * Techs asked for "arrows on the leak" / "circle the damage" so they
 * can call out details visually instead of typing "the leak is behind
 * that pipe, second from left". Loads a photo onto a canvas, lets the
 * user draw pen/arrow/circle/rect/text in red/yellow/blue/white, then
 * on Save flattens the drawing onto the image and uploads it back to
 * the job as a new photo with `kind=annotated`.
 *
 * Design decisions:
 *   • Flatten-and-upload (not editable overlay) — simpler, works on
 *     every downstream viewer (email, PDF export, ClickUp sync).
 *   • Original photo stays untouched — evidence-grade `_put_photo_with_evidence`
 *     already preserves it on the server side.
 *   • Tools mirror CompanyCam's + Slack Draw so it feels familiar.
 *
 * Props:
 *   src        — public URL of the photo to edit (usually mediaSrc(photo))
 *   jobId      — WO to attach the annotated result to
 *   onClose()  — called on cancel + save success
 *   onSaved(p) — optional; called with the new photo entry after upload
 */
export default function PhotoMarkupModal({ src, jobId, onClose, onSaved, uploadEndpoint = null, returnBlob = false }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const imgRef = useRef(null);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#EF4444");
  const [strokes, setStrokes] = useState([]); // committed list
  const [inProgress, setInProgress] = useState(null); // { tool, points, color, ... }
  const [textPrompt, setTextPrompt] = useState(null); // { x, y, value }
  const [saving, setSaving] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  const COLORS = [
    { name: "Red",    v: "#EF4444" },
    { name: "Yellow", v: "#FACC15" },
    { name: "Cyan",   v: "#06B6D4" },
    { name: "Lime",   v: "#84CC16" },
    { name: "White",  v: "#FFFFFF" },
    { name: "Black",  v: "#0F172A" },
  ];

  // Load the image once and cache its natural dimensions so we can size
  // both the base canvas and the interactive overlay identically.
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => toast.error("Couldn't load this photo for markup");
    img.src = src;
  }, [src]);

  // Every time the strokes list or in-progress stroke changes, repaint
  // the entire canvas: background image → committed strokes → current.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !imgRef.current || !imgSize.w) return;
    cv.width = imgSize.w;
    cv.height = imgSize.h;
    const ctx = cv.getContext("2d");
    ctx.drawImage(imgRef.current, 0, 0, imgSize.w, imgSize.h);
    // Reasonable stroke sizing based on image dimensions — a fixed 4px
    // pen looks fine on 800×600 but invisible on 4032×3024 iPhone shots.
    const strokeW = Math.max(4, Math.round(imgSize.w * 0.006));
    const arrowW  = Math.max(6, Math.round(imgSize.w * 0.010));
    const textPx  = Math.max(28, Math.round(imgSize.w * 0.035));
    const paintStroke = (s) => {
      ctx.strokeStyle = s.color;
      ctx.fillStyle   = s.color;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (s.tool === "pen") {
        ctx.lineWidth = strokeW;
        ctx.beginPath();
        (s.points || []).forEach((p, i) => {
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();
      } else if (s.tool === "arrow") {
        ctx.lineWidth = arrowW;
        const [a, b] = [s.points[0], s.points[s.points.length - 1]];
        if (!a || !b) return;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        // Arrowhead — two lines from tip at ±25° back
        const ang = Math.atan2(b.y - a.y, b.x - a.x);
        const head = Math.max(30, Math.round(imgSize.w * 0.03));
        ctx.beginPath();
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 7), b.y - head * Math.sin(ang - Math.PI / 7));
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 7), b.y - head * Math.sin(ang + Math.PI / 7));
        ctx.stroke();
      } else if (s.tool === "circle") {
        ctx.lineWidth = strokeW;
        const [a, b] = [s.points[0], s.points[s.points.length - 1]];
        if (!a || !b) return;
        const cx = (a.x + b.x) / 2;
        const cy = (a.y + b.y) / 2;
        const rx = Math.abs(b.x - a.x) / 2;
        const ry = Math.abs(b.y - a.y) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (s.tool === "rect") {
        ctx.lineWidth = strokeW;
        const [a, b] = [s.points[0], s.points[s.points.length - 1]];
        if (!a || !b) return;
        ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
      } else if (s.tool === "text" && s.text) {
        // Background pill behind text so it reads on busy photos
        ctx.font = `900 ${textPx}px "Helvetica Neue", Arial, sans-serif`;
        const metrics = ctx.measureText(s.text);
        const w = metrics.width + 24;
        const h = textPx * 1.4;
        ctx.fillStyle = "rgba(15, 23, 42, 0.75)";
        ctx.fillRect(s.points[0].x - 12, s.points[0].y - textPx, w, h);
        ctx.fillStyle = s.color;
        ctx.fillText(s.text, s.points[0].x, s.points[0].y + textPx * 0.1);
      }
    };
    strokes.forEach(paintStroke);
    if (inProgress) paintStroke(inProgress);
  }, [strokes, inProgress, imgSize]);

  // Translate a pointer event into image-pixel coordinates. The canvas
  // is rendered at natural resolution but scaled down via CSS to fit
  // the modal — event.offsetX is CSS pixels, we need image pixels.
  const evtToPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = imgSize.w / rect.width;
    const scaleY = imgSize.h / rect.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * scaleX, y: (cy - rect.top) * scaleY };
  };

  const onDown = (e) => {
    if (!imgSize.w) return;
    e.preventDefault();
    const pt = evtToPoint(e);
    if (tool === "text") {
      setTextPrompt({ x: pt.x, y: pt.y, value: "" });
      return;
    }
    setInProgress({ tool, color, points: [pt] });
  };
  const onMove = (e) => {
    if (!inProgress) return;
    e.preventDefault();
    const pt = evtToPoint(e);
    setInProgress(prev => {
      if (!prev) return prev;
      // Pen accumulates all points; shape tools keep just the two endpoints.
      const points = prev.tool === "pen" ? [...prev.points, pt] : [prev.points[0], pt];
      return { ...prev, points };
    });
  };
  const onUp = () => {
    if (!inProgress) return;
    setStrokes(prev => [...prev, inProgress]);
    setInProgress(null);
  };

  const commitText = () => {
    if (!textPrompt || !textPrompt.value.trim()) { setTextPrompt(null); return; }
    setStrokes(prev => [...prev, {
      tool: "text",
      color,
      points: [{ x: textPrompt.x, y: textPrompt.y }],
      text: textPrompt.value.trim(),
    }]);
    setTextPrompt(null);
  };

  const undo = () => setStrokes(prev => prev.slice(0, -1));
  const clearAll = () => setStrokes([]);

  const save = async () => {
    if (strokes.length === 0) {
      toast.error("Draw something first, then Save");
      return;
    }
    setSaving(true);
    try {
      const cv = canvasRef.current;
      const blob = await new Promise((resolve) => cv.toBlob(resolve, "image/jpeg", 0.9));
      if (!blob) throw new Error("Couldn't export image");
      // Caller wants the raw flattened blob back — no server round-trip.
      // Used by Quick-Capture-Fab so the tech can mark up BEFORE picking
      // a WO, and the annotated version becomes the file that uploads.
      if (returnBlob) {
        onSaved && onSaved({ blob });
        onClose();
        return;
      }
      const token = localStorage.getItem("access_token") || "";
      // Custom endpoint (e.g. inspection markup goes to a different
      // collection than job markup). Bypasses the offline queue since
      // markup is usually done live in an admin context; leaving offline
      // queue for the vanilla per-job case only.
      if (uploadEndpoint) {
        const fd = new FormData();
        fd.append("file", new File([blob], `markup-${Date.now()}.jpg`, { type: "image/jpeg" }));
        const { data } = await api.post(uploadEndpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Markup saved");
        onSaved && onSaved(data);
        onClose();
        return;
      }
      const { queued } = await tryLiveOrQueue(
        ({ token }) => makeMarkupUpload({ jobId, blob, token }),
        token,
      );
      if (queued) {
        toast("📶 Offline — markup queued, will upload when signal returns");
      } else {
        toast.success("Markup saved to job");
      }
      onSaved && onSaved({ queued });
      onClose();
    } catch (e) {
      toast.error(`Couldn't save: ${e?.response?.data?.detail || e.message || "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/95 flex flex-col" data-testid="photo-markup-modal">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 p-2 border-b border-white/10 flex-wrap bg-black/60">
        <button onClick={onClose} data-testid="markup-close"
          className="h-9 w-9 flex items-center justify-center text-white hover:bg-white/10 rounded">
          <X size={20}/>
        </button>
        <div className="text-white/60 text-[10px] font-black uppercase tracking-widest mx-2 hidden sm:inline">Markup</div>
        <div className="h-6 w-px bg-white/15 mx-1"/>
        <ToolBtn label="Pen"    icon={<Pencil size={16} weight="bold"/>}       active={tool === "pen"}    onClick={() => setTool("pen")}    testid="markup-tool-pen"/>
        <ToolBtn label="Arrow"  icon={<ArrowUpRight size={16} weight="bold"/>} active={tool === "arrow"}  onClick={() => setTool("arrow")}  testid="markup-tool-arrow"/>
        <ToolBtn label="Circle" icon={<CircleIcon size={16} weight="bold"/>}   active={tool === "circle"} onClick={() => setTool("circle")} testid="markup-tool-circle"/>
        <ToolBtn label="Box"    icon={<Square size={16} weight="bold"/>}       active={tool === "rect"}   onClick={() => setTool("rect")}   testid="markup-tool-rect"/>
        <ToolBtn label="Text"   icon={<TextT size={16} weight="bold"/>}        active={tool === "text"}   onClick={() => setTool("text")}   testid="markup-tool-text"/>
        <div className="h-6 w-px bg-white/15 mx-1"/>
        <div className="inline-flex items-center gap-1 px-1" data-testid="markup-colors">
          <Palette size={14} weight="fill" className="text-white/60 mr-0.5"/>
          {COLORS.map(c => (
            <button key={c.v} onClick={() => setColor(c.v)}
              data-testid={`markup-color-${c.name.toLowerCase()}`}
              title={c.name}
              className={`h-6 w-6 rounded-full border-2 transition ${color === c.v ? "border-white scale-110" : "border-white/20 hover:border-white/60"}`}
              style={{ backgroundColor: c.v }}/>
          ))}
        </div>
        <div className="h-6 w-px bg-white/15 mx-1"/>
        <button onClick={undo} disabled={strokes.length === 0}
          data-testid="markup-undo"
          className="h-9 px-2 text-white/80 hover:bg-white/10 disabled:opacity-40 rounded inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest">
          <ArrowClockwise size={14} weight="bold" style={{ transform: "scaleX(-1)" }}/> Undo
        </button>
        <button onClick={clearAll} disabled={strokes.length === 0}
          data-testid="markup-clear"
          className="h-9 px-2 text-white/60 hover:bg-white/10 disabled:opacity-40 rounded inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest">
          <Trash size={14} weight="bold"/> Clear
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={save} disabled={saving || strokes.length === 0}
            data-testid="markup-save"
            className="h-10 px-4 bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs rounded inline-flex items-center gap-1.5">
            <Check size={14} weight="bold"/>
            {saving ? "Saving…" : "Save Markup"}
          </button>
        </div>
      </div>
      {/* Canvas area */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4 relative" data-testid="markup-canvas-wrap">
        {imgSize.w > 0 ? (
          <div className="relative inline-block max-w-full max-h-full" style={{ touchAction: "none" }}>
            <canvas
              ref={canvasRef}
              onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
              onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
              data-testid="markup-canvas"
              className="max-w-full max-h-[75vh] shadow-2xl border border-white/10"
              style={{ cursor: tool === "text" ? "text" : "crosshair" }}
            />
            {textPrompt && (
              <div className="absolute z-10 flex items-center gap-2 bg-white/95 rounded shadow-xl p-2"
                data-testid="markup-text-prompt"
                style={{ left: Math.max(0, (textPrompt.x / imgSize.w) * 100) + "%", top: Math.max(0, (textPrompt.y / imgSize.h) * 100) + "%", transform: "translate(-50%, -100%)" }}>
                <input
                  autoFocus
                  value={textPrompt.value}
                  onChange={(e) => setTextPrompt({ ...textPrompt, value: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") commitText(); if (e.key === "Escape") setTextPrompt(null); }}
                  placeholder="Type label…"
                  className="h-8 w-40 px-2 text-sm font-bold text-[#0F1F3A] focus:outline-none border-2 border-slate-300 rounded"
                  data-testid="markup-text-input"
                />
                <button onClick={commitText} className="h-8 px-3 bg-[#00C4CC] text-white font-black uppercase tracking-widest text-[10px] rounded"
                        data-testid="markup-text-commit">
                  Add
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-white/60 text-sm font-bold">Loading photo…</div>
        )}
      </div>
      {/* Footer hint */}
      <div className="p-2 text-center text-[10px] font-black uppercase tracking-widest text-white/50 border-t border-white/5">
        Original is preserved — this saves a new marked-up copy to the job
      </div>
    </div>
  );
}

function ToolBtn({ label, icon, active, onClick, testid }) {
  return (
    <button onClick={onClick} title={label} data-testid={testid}
      className={`h-9 px-2 inline-flex items-center gap-1.5 rounded text-xs font-black uppercase tracking-widest transition ${
        active ? "bg-white text-[#0F1F3A]" : "text-white/80 hover:bg-white/10"
      }`}>
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
